<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">

    <h1 class="text-3xl font-bold text-gray-900 sm:text-[24px] leading-7 mb-12">
      Permissions
    </h1>

    <!-- Search + New User -->
    <div
      class="flex flex-col gap-4 mb-12 sm:flex-row sm:items-center sm:justify-between"
    >
      <div class="w-full sm:w-64">
        <input
          v-model="search"
          type="text"
          placeholder="Search"
          class="w-64 h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none"
        />
      </div>

      <div class="flex items-center gap-3 sm:justify-end">
        <button
          v-if="can.create"
          type="button"
          class="inline-flex items-center gap-2 h-9 px-4 rounded-full text-white font-bold text-sm shadow-sm hover:opacity-95"
          :style="{ backgroundColor: '#5B9AC5', whiteSpace: 'nowrap' }"
          @click="openAddModal"
        >
          + New user
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <DynamicTable
        :rows="rows"
        :columns="columns"
        :rowsPerPage="rows.length || 1000"
        :rowsPerPageOptions="[1000]"
        :showHeader="false"
        :showAddButton="false"
        :externalSearch="search"
        :rowClass="rowClass"
      >
        <template #action="{ row }">
          <div class="flex items-center justify-center gap-3 text-[13px]">
            <template v-if="row.id !== 1">
              <button
                v-if="can.edit"
                class="flex items-center gap-1 text-gray-700"
                @click="goEdit(row)"
              >
                <svg viewBox="0 0 24 24" class="w-4 h-4">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z"
                    fill="currentColor"
                  />
                </svg>
                <span>Edit</span>
              </button>
              <button
                v-if="can.delete"
                class="flex items-center gap-1 text-black"
                @click="openDelete(row)"
              >
                <i class="fas fa-trash-alt text-red-600"></i>
                <span>Delete</span>
              </button>
            </template>
          </div>
        </template>
      </DynamicTable>
    </div>

    <!-- Add User Modal -->
    <teleport to="body">
      <div
        v-if="showAdd"
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
        :key="addModalKey"
      >
        <div class="bg-white w-[92%] max-w-md rounded-2xl shadow p-5 relative">
          <button
            class="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
            @click="closeAddModal"
            type="button"
            aria-label="Close"
          >
            ✕
          </button>

          <h3 class="text-lg font-bold text-center mb-5">Add User</h3>

          <form @submit.prevent="submitAdd" class="space-y-4" autocomplete="off" novalidate>
            <!-- Employee -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">
                Employee
              </label>
              <select
                ref="add_employee_id"
                v-model="addForm.user_id"
                class="w-full h-10 rounded-xl border-[2px] px-3 text-sm focus:outline-none"
                :class="inputClass('user_id')"
                name="new_employee"
                autocomplete="off"
                @blur="touch('user_id')"
              >
                <option value="" disabled>Select employee</option>
                <option
                  v-for="emp in employees"
                  :key="emp.id"
                  :value="String(emp.id)"
                >
                  {{ emp.name }}
                </option>
              </select>
              <p v-if="showError('user_id')" class="text-red-600 text-xs mt-1">
                {{ errors.user_id }}
              </p>
            </div>

            <!-- Password -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">
                Password
              </label>
              <div class="relative">
                <input
                  ref="add_password"
                  v-model="addForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="w-full h-10 rounded-xl border-[2px] px-3 pr-10 text-sm focus:outline-none"
                  :class="inputClass('password')"
                  placeholder="Enter password"
                  name="new_password"
                  autocomplete="new-password"
                  @blur="touch('password')"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                  aria-label="Toggle password visibility"
                >
                  <!-- Eye (hidden) -->
                  <svg
                    v-if="!showPassword"
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>

                  <!-- Eye off (visible) -->
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.88"
                    />
                    <path
                      d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-3.24 4.23"
                    />
                    <path d="m1 1 22 22" />
                  </svg>
                </button>
              </div>
              <p v-if="showError('password')" class="text-red-600 text-xs mt-1">
                {{ errors.password }}
              </p>
            </div>

            <!-- Confirm Password -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">
                Confirm Password
              </label>
              <div class="relative">
                <input
                  ref="add_password_confirmation"
                  v-model="addForm.password_confirmation"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="w-full h-10 rounded-xl border-[2px] px-3 pr-10 text-sm focus:outline-none"
                  :class="inputClass('password_confirmation')"
                  placeholder="Confirm password"
                  name="new_password_confirm"
                  autocomplete="new-password"
                  @blur="touch('password_confirmation')"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                  @click="showConfirmPassword = !showConfirmPassword"
                  tabindex="-1"
                  aria-label="Toggle confirm password visibility"
                >
                  <svg
                    v-if="!showConfirmPassword"
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>

                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-6.88"
                    />
                    <path
                      d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-3.24 4.23"
                    />
                    <path d="m1 1 22 22" />
                  </svg>
                </button>
              </div>
              <p
                v-if="showError('password_confirmation')"
                class="text-red-600 text-xs mt-1"
              >
                {{ errors.password_confirmation }}
              </p>
            </div>

            <button
              type="submit"
              class="w-full h-10 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-60 disabled:pointer-events-none"
              :style="{ backgroundColor: '#5B9AC5' }"
              :disabled="submitting"
            >
              <span v-if="!submitting">Add</span>
              <span v-else>Saving...</span>
            </button>
          </form>
        </div>
      </div>
    </teleport>

    <!-- Delete Modal -->
    <teleport to="body">
      <transition name="fade">
        <div
          v-if="showDelete"
          class="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
          @click.self="cancelDelete"
          @keydown.esc="cancelDelete"
          tabindex="0"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-title"
        >
          <div
            class="bg-white rounded-[28px] w-full max-w-lg shadow-xl p-8 sm:p-10"
          >
            <div
              class="mx-auto mb-6 w-16 h-16 rounded-full bg-red-500 grid place-items-center shadow-md"
            >
              <svg viewBox="0 0 24 24" class="w-7 h-7 text-white">
                <path
                  fill="currentColor"
                  d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.3 19.71 2.89 18.3 9.17 12 2.89 5.71 4.3 4.29 10.59 10.6 16.89 4.29z"
                />
              </svg>
            </div>
            <h2
              id="delete-title"
              class="text-center text-lg sm:text=[15px] leading-7 font-bold text-gray-900 mb-2"
            >
              Are you sure to
            </h2>
            <p
              class="text-center text-lg sm:text=[15px] leading-7 font-bold text-gray-900"
            >
              delete this User?
            </p>
            <div class="mt-8 flex items-center justify-between">
              <button
                type="button"
                class="h-10 px-6 rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
                @click="cancelDelete"
              >
                Cancel
              </button>
              <button
                type="button"
                class="h-10 px-8 rounded-full text-white shadow-sm hover:opacity-95"
                :style="{ backgroundColor: '#5B9AC5' }"
                @click="confirmDelete"
              >
                yes
              </button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import DynamicTable from "@/components/DynamicTable.vue";
const MOCK_USERS = [
  { id: 1, name: "Main Admin", email: "admin@example.com" },
  { id: 2, name: "John Doe", email: "john@example.com" },
  { id: 3, name: "Sara Smith", email: "sara@example.com" },
];

const MOCK_EMPLOYEES = [
  { id: "2", name: "John Doe" },
  { id: "3", name: "Sara Smith" },
];

export default {
  name: "PermissionIndex",
  components: { DynamicTable },

  data() {
    return {
      can: {
        view: true,
        create: true,
        edit: true,
        delete: true,
      },

      search: "",
      rows: [],
      columns: [
        { key: "id", label: "ID", width: "314px" },
        { key: "name", label: "Name", width: "315px" },
        { key: "email", label: "Email", width: "315px" },
        { key: "action", label: "Action", width: "300px" },
      ],

      showAdd: false,
      addForm: { user_id: "", password: "", password_confirmation: "" },
      errors: { user_id: "", password: "", password_confirmation: "" },
      touched: { user_id: false, password: false, password_confirmation: false },
      submitting: false,
      addModalKey: 0,
      showPassword: false,
      showConfirmPassword: false,
      employees: [],
      showDelete: false,
      rowToDelete: null,
    };
  },

  created() {
    this.fetchRows();
    this.fetchEmployees();
  },

  methods: {
    rowClass() {
      return "";
    },
    async fetchRows() {
      this.rows = [...MOCK_USERS];
    },

    async fetchEmployees() {
      this.employees = [...MOCK_EMPLOYEES];
    },

    validateField(field) {
      const v = this.addForm[field] || "";
      let msg = "";

      if (field === "user_id") {
        if (!v) msg = "Employee is required.";
      }
      if (field === "password") {
        if (!v) msg = "Password is required.";
        else if (v.length < 6) msg = "Password must be at least 6 characters.";
      }
      if (field === "password_confirmation") {
        if (!v) msg = "Please confirm the password.";
        else if (v !== this.addForm.password) msg = "Passwords do not match.";
      }

      this.errors[field] = msg;
      return !msg;
    },
    validateAll() {
      const fields = ["user_id", "password", "password_confirmation"];
      fields.forEach((f) => (this.touched[f] = true));
      const results = fields.map((f) => this.validateField(f));
      return results.every(Boolean);
    },
    touch(field) {
      this.touched[field] = true;
      this.validateField(field);
    },
    showError(field) {
      return this.touched[field] && !!this.errors[field];
    },
    inputClass(field) {
      return this.showError(field)
        ? "border-red-500 focus:border-red-600"
        : "border-gray-300 focus:border-gray-400";
    },
    focusFirstError() {
      const order = ["user_id", "password", "password_confirmation"];
      for (const f of order) {
        if (this.errors[f]) {
          const refName = f === "user_id" ? "add_employee_id" : `add_${f}`;
          this.$nextTick(() => {
            const el = this.$refs[refName];
            if (el?.focus) el.focus();
          });
          break;
        }
      }
    },

    // ---------- Add Modal ----------
    openAddModal() {
      if (!this.can.create) return;

      this.addForm = {
        user_id: "",
        password: "",
        password_confirmation: "",
      };
      this.errors = {
        user_id: "",
        password: "",
        password_confirmation: "",
      };
      this.touched = {
        user_id: false,
        password: false,
        password_confirmation: false,
      };
      this.addModalKey++;
      this.showAdd = true;
      document.body.style.overflow = "hidden";
    },
    closeAddModal() {
      this.showAdd = false;
      document.body.style.overflow = "";
    },

    async submitAdd() {
      if (!this.can.create) return;

      if (!this.validateAll()) {
        this.focusFirstError();
        return;
      }

      const emp = this.employees.find(
        (e) => String(e.id) === String(this.addForm.user_id)
      );
      if (!emp) {
        this.errors.user_id = "Please select an employee.";
        this.touched.user_id = true;
        return;
      }

      const newId =
        (this.rows.length ? Math.max(...this.rows.map((r) => r.id)) : 0) + 1;
      this.rows.push({
        id: newId,
        name: emp.name,
        email: `${emp.name.toLowerCase().replace(/\s+/g, ".")}@example.com`,
      });
      this.closeAddModal();
    },

    goEdit(row) {
      if (row.id === 1 || !this.can.edit) return;
      this.$router.push({ name: "PermissionEdit", params: { id: row.id } });
    },
    openDelete(row) {
      if (row.id === 1 || !this.can.delete) return;
      this.rowToDelete = row;
      this.showDelete = true;
      document.body.style.overflow = "hidden";
    },
    cancelDelete() {
      this.showDelete = false;
      this.rowToDelete = null;
      document.body.style.overflow = "";
    },
    async confirmDelete() {
      if (!this.rowToDelete || !this.can.delete) return;
      const id = this.rowToDelete.id;
      this.rows = this.rows.filter((u) => u.id !== id);
      this.cancelDelete();
    },
  },
};
</script>

<style scoped>
    @reference "tailwindcss";
:deep(.flex.justify-center.items-center.gap-2.py-5) {
  display: none;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:deep(td) {
  @apply text-base;
}
:deep(table) {
  border-collapse: collapse;
  table-layout: auto;
  min-width: max-content;
}
:deep(th),
:deep(td) {
  white-space: nowrap;
}
:deep(th.truncate),
:deep(td.truncate) {
  overflow: visible;
  text-overflow: clip;
}
@media (max-width: 768px) {
  :deep(th),
  :deep(td) {
    font-size: 0.75rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>
